import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Activity, AlertTriangle, BarChart3, Wand2, Library, Mail, MessageSquareText, Sparkles, Trophy } from 'lucide-react';
import { Sidebar, type SidebarItem, type SidebarGroup } from './Sidebar';
import { MotesGlyf } from '@/components/features/moten/MotesDelar';
import { MOTEN_SLAPPT } from '../../../netlify/functions/_shared/moten/slapp';
import { Header } from './Header';
import { useAdmin } from '@/hooks/useAdmin';
import { useSalesPreview } from '@/hooks/useSalesPreview';
import { useBackdrop } from '@/hooks/useBackdrop';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/lib/utils';

/**
 * Menyn är grupperad i stället för att vara en platt lista. Åtta likvärdiga
 * poster tvingar läsaren att läsa alla åtta varje gång; fyra rubriker gör att
 * ögat kan hoppa till rätt område direkt.
 */
const items: SidebarItem[] = [
  { to: '/', label: 'Insikter', icon: BarChart3, group: 'arbeta' },
  { to: '/ai-analys', label: 'ELvis', icon: Wand2, group: 'arbeta' },
  { to: '/mail', label: 'Elvira', icon: Mail, group: 'arbeta' },
  { to: '/moten', label: 'Möten', icon: MotesGlyf, group: 'arbeta', badge: MOTEN_SLAPPT ? undefined : 'Snart' },
  { to: '/kunskapsbas', label: 'Kunskapsbas', icon: Library, group: 'kunskap' },
  { to: '/granskning', label: 'Granskning', icon: AlertTriangle, group: 'kunskap' },
  { to: '/status', label: 'Systemstatus', icon: Activity, group: 'drift' },
  { to: '/feedback', label: 'Feedback', icon: MessageSquareText, group: 'drift' },
  { to: '/kretskampen', label: 'Kretskampen', icon: Trophy, group: 'internt' },
  { to: '/nasta-steg', label: 'Nästa steg', icon: Sparkles, group: 'internt', presentationOnly: true },
];

const groups: SidebarGroup[] = [
  { key: 'arbeta', label: 'Arbeta' },
  { key: 'kunskap', label: 'Kunskap' },
  { key: 'drift', label: 'Drift' },
  { key: 'internt', label: 'Internt' },
];

/** Titeln i topbaren följer rutten, så varje sida slipper rita en egen rubrikrad. */
const titles: Record<string, string> = {
  '/': 'Insikter',
  '/ai-analys': 'ELvis',
  '/mail': 'Elvira',
  '/moten': 'Möten',
  '/kunskapsbas': 'Kunskapsbas',
  '/granskning': 'Granskning',
  '/status': 'Systemstatus',
  '/feedback': 'Feedback',
  '/kretskampen': 'Kretskampen',
  '/nasta-steg': 'Nästa steg',
};

const STORAGE_KEY = 'elvis-sidebar-collapsed';

export function Shell() {
  const { isAdmin } = useAdmin();
  const { canSee: canSeePresentation } = useSalesPreview();
  const { enabled: backdrop, toggle: toggleBackdrop } = useBackdrop();
  const { morkt, toggle: toggleTema } = useTheme();
  const { pathname } = useLocation();

  const visibleItems = items.filter(
    (item) => (!item.adminOnly || isAdmin) && (!item.presentationOnly || canSeePresentation),
  );

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  // Mobilmenyn stängs vid varje ruttbyte, även när bytet inte kom från menyn
  // (bakåtknappen, en länk i innehållet). Nollställs under renderingen – Reacts
  // mönster för state som följer en prop – i stället för i en effect.
  const [mobilMeny, setMobilMeny] = useState(false);
  const [menyRutt, setMenyRutt] = useState(pathname);
  if (menyRutt !== pathname) {
    setMenyRutt(pathname);
    setMobilMeny(false);
  }
  // Stabil referens: Sidebar har stängningen i en effects beroenden.
  const stangMobilMeny = useCallback(() => setMobilMeny(false), []);

  // Längsta matchande prefix vinner, så undersidor ärver sin flikrubrik.
  const title =
    titles[pathname] ??
    Object.entries(titles)
      .filter(([path]) => path !== '/' && pathname.startsWith(path))
      .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ??
    'ELvis Hub';

  return (
    // Grundfärgen måste bort när bilden är på, annars ligger den över den
    <div className={cn('flex h-screen overflow-hidden', !backdrop && 'bg-canvas')}>
      {backdrop && <div className="app-backdrop" />}

      <Sidebar
        items={visibleItems}
        groups={groups}
        collapsed={collapsed}
        onToggle={() => setCollapsed((c) => !c)}
        backdrop={backdrop}
        onToggleBackdrop={toggleBackdrop}
        morkt={morkt}
        onToggleTema={toggleTema}
        mobilOppen={mobilMeny}
        onStangMobil={stangMobilMeny}
      />

      <div className="flex-1 flex flex-col min-w-0">
        <Header title={title} menyOppen={mobilMeny} onMeny={() => setMobilMeny((m) => !m)} />
        <main className="flex-1 flex flex-col overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
